import { useEffect, useMemo, useState } from 'react'
import {
  Search, Loader2, X, Coins, Calculator, Download, AlertTriangle, Lock, Stethoscope, Info,
  ChevronUp, ChevronDown, ChevronsUpDown,
} from 'lucide-react'
import { apiGet, cachedGet, postJson } from '../../lib/api.js'
import { usePersistedState } from '../../lib/persist.js'
import { nameInitial } from '../../lib/text.js'
import { useMe } from '../../lib/me.js'
import { isAdmin as isAdminRole } from '../../lib/role.js'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'
import SalaryDetailDrawer from '../../components/dentist/SalaryDetailDrawer.jsx'
import DfTransferModal from '../../components/dentist/DfTransferModal.jsx'
import { doctorCode, normText } from '../../lib/dentist.js'
import {
  fmtMoney, TH_MONTHS, SALARY_YEARS, lastMonth, periodLabel, totals,
  rowTotal, rowDF, rowLab, rowTrace, rowNonTrace, rowTransfer,
  isSettled, settledInfo, checkBalance,
} from '../../lib/salaryDoctor.js'

const SELECT = 'rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-700 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

// คำนวณรายได้ is HIDDEN as of 2026-09-06, by Ram's call: the month is generated
// from Xano directly (POST df_doctor/gen2, api 419) rather than from here. The
// button, its confirm dialog and runGen() below are left wired and working —
// flip this to `true` to bring it back. Everything it warns about still holds:
// the run rewrites the whole month's payroll and pulls earlier months' unsettled
// in-house labs into it. Background in functions/api/salary-doctor/gen.js.
const SHOW_GENERATE = false

// The two columns the clinic actually pays out on, tinted so they read as a pair.
const PAID = 'bg-brand-50/40'

// Sortable money columns. Defaults to รายได้รวม desc — the order the exported
// สรุป DF file uses — so the screen and the file line up until you sort.
const SORTS = {
  total: rowTotal, df: rowDF, trace: rowTrace,
  nontrace: rowNonTrace, lab: rowLab, transfer: rowTransfer,
}

/**
 * Manager › ทันตแพทย์ › จัดการรายได้แพทย์ — the monthly DF payroll for doctors.
 *
 * One `salary_doctor` row per doctor per month. The page reads the whole month at
 * once (it is ~40 rows) and totals it client-side; there is no server paging to
 * fight. See lib/salaryDoctor.js for the money model — in particular why
 * `df_trace + transfer` is the payout and `df_trace + df_nontrace` is not.
 *
 * Writes (คำนวณ / ดาวน์โหลด) are Manager/Admin, narrower than the Manager area
 * itself: the proxies 403 a Stock Manager.
 */
export default function DentistIncome() {
  const me = useMe()
  const canRun = isAdminRole(me) || hasManagerRole(me)

  const init = lastMonth()
  const [year, setYear] = usePersistedState('docsal:year', String(init.year))
  const [month, setMonth] = usePersistedState('docsal:month', String(init.month))
  const [search, setSearch] = usePersistedState('docsal:search', '')
  const [sortKey, setSortKey] = usePersistedState('docsal:sortKey', 'total')
  const [sortDir, setSortDir] = usePersistedState('docsal:sortDir', 'desc')

  const [rows, setRows] = useState([])
  const [doctors, setDoctors] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [open, setOpen] = useState(null)

  const [asking, setAsking] = useState(false)
  // ดาวน์โหลด (ZIP) asks for the value date first — it is the day the money
  // leaves and SCB refuses a past one. See DfTransferModal.
  const [downloading, setDownloading] = useState(false)
  const [running, setRunning] = useState(false)
  const [runMsg, setRunMsg] = useState('')
  const [runErr, setRunErr] = useState('')

  useEffect(() => {
    cachedGet('/api/doctors', { slim: 1 })
      .then((r) => setDoctors(Array.isArray(r) ? r : (r?.items || [])))
      .catch(() => {})
  }, [])

  const docById = useMemo(() => {
    const m = new Map()
    for (const d of doctors) m.set(Number(d.id), d)
    return m
  }, [doctors])

  function load() {
    setLoading(true); setErr('')
    apiGet('/api/salary-doctor', { year, month })
      .then((r) => setRows(Array.isArray(r) ? r : (r?.items || [])))
      .catch((e) => setErr(String(e.message || e)))
      .finally(() => setLoading(false))
  }
  useEffect(load, [year, month]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRunMsg(''); setRunErr('') }, [year, month])

  // Naming comes from the row itself: GET /salary_doctor resolves a `doctor`
  // addon ({name, surname, code}) — so a row stays readable even for a doctor
  // that has since left the directory. The /api/doctors lookup is only for the
  // avatar and the second bank account, neither of which the addon carries.
  const enriched = useMemo(() => rows.map((r) => {
    const id = Number(r.doctor_id) || 0
    const d = id ? docById.get(id) : null
    const embedded = r.doctor?.code || [r.doctor?.name, r.doctor?.surname].filter(Boolean).join(' ')
    return {
      ...r,
      _doc: d,
      _orphan: !id,   // see the ORPHAN banner below
      _code: embedded
        || (d ? doctorCode(d) : '')
        || (id ? `หมอ #${id}` : (r.banknumber ? `บัญชี ${r.banknumber}` : 'ไม่ทราบชื่อหมอ')),
    }
  }), [rows, docById])

  // ⚠️ Rows whose `doctor_id` is 0 are not attributable to a doctor. Observed on
  // the whole of 2026-06 (203/203) while 03–05 are clean, so it is a defect in
  // that generation run, not a display problem: the payout files built by
  // df_doctor/export2 look the doctor up BY id, so those rows would export with
  // no name and no traceable account.
  const orphans = useMemo(() => enriched.filter((r) => r._orphan).length, [enriched])

  const shown = useMemo(() => {
    const q = normText(search)
    const list = q
      ? enriched.filter((r) => normText(
          [r._code, r.doctor?.name, r.doctor?.surname, r.banknumber].filter(Boolean).join(' '),
        ).includes(q))
      : enriched
    const pick = SORTS[sortKey] || rowTotal
    const dir = sortDir === 'asc' ? 1 : -1
    // รายได้รวม breaks ties so the order is stable when a column is mostly zeros
    // (ค่า Lab in particular) rather than shuffling on every render.
    return [...list].sort((a, b) => dir * (pick(a) - pick(b)) || rowTotal(b) - rowTotal(a))
  }, [enriched, search, sortKey, sortDir])

  const sum = useMemo(() => totals(shown), [shown])
  const anySettled = shown.some(isSettled)
  // Cross-foot every visible row: df_trace + lab + transfer must equal df.
  const balanceFails = useMemo(() => shown.filter((r) => !checkBalance(r).ok).length, [shown])

  // A new column starts descending (biggest first is what a manager wants);
  // clicking the active one flips it.
  function toggleSort(k) {
    if (k === sortKey) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    else { setSortKey(k); setSortDir('desc') }
  }
  const sortProps = { sortKey, sortDir, onSort: toggleSort }

  async function runGen() {
    setAsking(false); setRunning(true); setRunErr(''); setRunMsg('')
    try {
      const res = await postJson('/api/salary-doctor/gen', { year: Number(year), month: Number(month) })
      setRunMsg(typeof res === 'string' && res ? res : 'คำนวณรายได้เรียบร้อย')
      load()
    } catch (e) {
      // The upstream precondition ("มีการรักษาที่ไม่มีชื่อหมอ โดยมี id = …") is the
      // actionable one — pass the message straight through.
      setRunErr(xanoMsg(e))
    } finally {
      setRunning(false)
    }
  }

  const period = periodLabel(year, month, TH_MONTHS)

  return (
    <div className="max-w-[1240px]">
      {/* period + actions */}
      <div className="flex flex-wrap items-center gap-2.5 mb-4">
        <select value={month} onChange={(e) => setMonth(e.target.value)} className={SELECT}>
          {TH_MONTHS.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
        </select>
        <select value={year} onChange={(e) => setYear(e.target.value)} className={SELECT}>
          {SALARY_YEARS.map((y) => <option key={y} value={String(y)}>{y + 543}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ค้นหาหมอ…"
            className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10"
          />
        </div>
        {search && (
          <button onClick={() => setSearch('')} className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-[13px] text-slate-500 hover:bg-slate-100">
            <X size={14} /> ล้าง
          </button>
        )}
        {canRun && (
          <>
            {SHOW_GENERATE && (
              <button
                onClick={() => setAsking(true)}
                disabled={running || loading || orphans > 0}
                title={orphans > 0 ? 'เดือนนี้มีข้อมูลผิดปกติ (ไม่มี doctor_id) — ดูคำเตือนด้านล่าง' : ''}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-[13.5px] font-medium text-slate-700 hover:border-brand-300 hover:text-brand-700 disabled:opacity-40"
              >
                {running ? <Loader2 size={15} className="animate-spin" /> : <Calculator size={15} />} คำนวณรายได้
              </button>
            )}
            <button
              onClick={() => setDownloading(true)}
              disabled={!rows.length || orphans > 0}
              title={orphans > 0 ? 'ไฟล์โอนเงินจะไม่มีชื่อ/บัญชีของหมอ — ดูคำเตือนด้านล่าง' : ''}
              className="inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-[13.5px] font-semibold text-white shadow-sm bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 disabled:hover:bg-slate-300"
            >
              <Download size={15} /> ดาวน์โหลด (ZIP)
            </button>
          </>
        )}
      </div>

      {runMsg && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-[13.5px] text-emerald-800">{runMsg}</div>
      )}
      {runErr && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-[13.5px] text-rose-700 flex items-start gap-2">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span className="whitespace-pre-wrap">{runErr}</span>
        </div>
      )}

      {orphans > 0 && (
        <div className="mb-4 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div className="space-y-1">
              <div className="font-semibold text-[13.5px]">
                ข้อมูลเดือนนี้ผิดปกติ — {orphans.toLocaleString()} จาก {rows.length.toLocaleString()} รายการ ไม่มีรหัสหมอ (doctor_id = 0)
              </div>
              <p className="leading-relaxed">
                ระบุตัวหมอไม่ได้ จึงแสดงเป็นเลขบัญชีแทน · <b>ไฟล์โอนเงินจะไม่มีชื่อและบัญชีของหมอ</b> เพราะไฟล์ดึงข้อมูลหมอจากรหัส
                — ปุ่มคำนวณและดาวน์โหลดจึงถูกปิดไว้เพื่อความปลอดภัย
              </p>
              <p className="leading-relaxed text-rose-700">
                ⚠️ ห้ามสั่งคำนวณเดือนนี้ซ้ำจนกว่าจะแก้ที่ Xano — การคำนวณจะจับคู่รายการเดิมไม่เจอ และจะ<b>สร้างรายการซ้ำเพิ่มอีกชุด</b>
                (จำนวนแถวที่มีอยู่บ่งชี้ว่ารอบก่อนก็ไม่ได้จับคู่เช่นกัน)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* summary */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2.5 mb-4">
        <Stat label="ทันตแพทย์" value={sum.doctors.toLocaleString()} unit="คน" />
        <Stat label="DF รวม" value={fmtMoney(sum.df)} />
        <Stat label="ค่า Lab" value={fmtMoney(sum.lab)} tone="amber" />
        <Stat label="DF (t)" value={fmtMoney(sum.trace)} hint="โอนเข้าบัญชีในระบบ" />
        <Stat label="DF (nt - lab)" value={fmtMoney(sum.transfer)} hint="โอนเข้าบัญชีนอกระบบ หลังหักค่า Lab" />
        {/* The figure that leaves the bank: DF (t) + DF (nt - lab), with any
            doctor whose DF (nt - lab) is negative left out entirely — they owe
            the clinic, so there is nothing to transfer to them. */}
        <Stat
          label="ยอดโอนทั้งหมด"
          value={fmtMoney(sum.payable)}
          tone="brand"
          hint={sum.negatives ? `ไม่รวมหมอติดลบ ${sum.negatives} คน` : 'DF (t) + DF (nt - lab)'}
        />
      </div>

      {/* table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="text-left text-[12px] font-medium text-slate-400 border-b border-slate-100">
                {/* Column order mirrors the exported สรุป DF csv, so the screen and
                    the file read the same way. DF trace + transfer are tinted:
                    together they are what the clinic pays out (= DF − LAB). */}
                <th className="px-4 py-2.5">ทันตแพทย์</th>
                <SortTh k="total"    label="รายได้รวม"     {...sortProps} />
                <SortTh k="df"       label="DF"            {...sortProps} />
                <SortTh k="trace"    label="DF (t)"        {...sortProps} className={PAID} />
                <SortTh k="nontrace" label="DF (nt)"       {...sortProps} />
                <SortTh k="lab"      label="ค่า Lab"        {...sortProps} />
                <SortTh k="transfer" label="DF (nt - lab)" {...sortProps} className={PAID} />
                <th className="px-4 py-2.5 whitespace-nowrap w-px">สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400"><Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…</td></tr>
              )}
              {!loading && err && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-rose-500">{err}</td></tr>
              )}
              {!loading && !err && !shown.length && (
                <tr>
                  <td colSpan={8} className="px-4 py-14 text-center">
                    <Coins size={26} className="mx-auto mb-2 text-slate-300" />
                    <div className="text-[14px] text-slate-500">ยังไม่มีข้อมูลรายได้ของ {period}</div>
                    {/* Only while the button exists — never point at a hidden one. */}
                    {SHOW_GENERATE && canRun && <div className="text-[12.5px] text-slate-400 mt-1">กด “คำนวณรายได้” เพื่อสร้างสรุปจากรายการรักษาในเดือนนี้</div>}
                  </td>
                </tr>
              )}
              {!loading && !err && shown.map((r) => {
                const s = settledInfo(isSettled(r))
                const owed = rowTransfer(r) < 0
                const bal = checkBalance(r)
                return (
                  <tr
                    key={r.id}
                    onClick={() => setOpen(r)}
                    className="border-b border-slate-50 last:border-0 hover:bg-brand-50/40 cursor-pointer transition"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar src={r._doc?.profile_img} name={r._code} />
                        <div className="min-w-0">
                          <div className={`font-medium truncate ${r._orphan ? 'text-rose-700' : 'text-slate-800'}`}>
                            {r._orphan && <AlertTriangle size={11} className="inline mr-1 -mt-0.5" />}{r._code}
                          </div>
                          {r.bank && <div className="text-[11.5px] text-slate-400 truncate">{r.bank} · {r.banknumber || '—'}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">{fmtMoney(rowTotal(r))}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-800 font-medium">
                      {fmtMoney(rowDF(r))}
                      {!bal.ok && (
                        <span title={`DF (t) + ค่า Lab + DF (nt - lab) ต่างจาก DF อยู่ ${fmtMoney(bal.diff)}`} className="ml-1 text-rose-600">⚠</span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-right tabular-nums font-medium text-slate-800 ${PAID}`}>{fmtMoney(rowTrace(r))}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-500">{fmtMoney(rowNonTrace(r))}</td>
                    <td className={`px-4 py-3 text-right tabular-nums ${rowLab(r) ? 'text-amber-700' : 'text-slate-300'}`}>
                      {rowLab(r) ? fmtMoney(rowLab(r)) : '—'}
                    </td>
                    <td className={`px-4 py-3 text-right tabular-nums font-medium ${PAID} ${owed ? 'text-rose-600' : 'text-slate-800'}`}>
                      {fmtMoney(rowTransfer(r))}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap w-px">
                      <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${s.tone}`}>
                        {isSettled(r) && <Lock size={10} />} {s.label}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            {!loading && !err && shown.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50/70 text-[13px] font-semibold text-slate-700">
                  <td className="px-4 py-2.5">รวม {sum.doctors} คน</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtMoney(sum.total)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtMoney(sum.df)}</td>
                  <td className={`px-4 py-2.5 text-right tabular-nums ${PAID}`}>{fmtMoney(sum.trace)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{fmtMoney(sum.nonTrace)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-amber-700">{sum.lab ? fmtMoney(sum.lab) : '—'}</td>
                  <td className={`px-4 py-2.5 text-right tabular-nums ${PAID}`}>{fmtMoney(sum.transfer)}</td>
                  <td className="px-4 py-2.5" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="mt-3 space-y-1.5">
        <p className="text-[12.5px] text-slate-600 flex items-start gap-1.5">
          <Info size={13} className="mt-0.5 shrink-0 text-slate-400" />
          <span>
            <b className="text-brand-700">DF (t)</b> + <b>ค่า Lab</b> + <b className="text-brand-700">DF (nt - lab)</b> = <b>DF</b>
            {balanceFails === 0
              ? <span className="text-emerald-700"> · ตรวจแล้วตรงทุกแถว ({shown.length.toLocaleString()} แถว)</span>
              : <span className="text-rose-600"> · ⚠ ไม่ตรง {balanceFails} แถว (ดูเครื่องหมายในคอลัมน์ DF)</span>}
          </span>
        </p>
        <p className="text-[12px] text-slate-400 flex items-start gap-1.5">
          <span className="w-[13px] shrink-0" />
          <span>
            ช่องที่ไฮไลต์คือยอดที่จ่ายจริง — โอน 2 รอบคนละบัญชี รวม {fmtMoney(sum.trace + sum.transfer)} (= DF − ค่า Lab) ·
            ค่า Lab หักจากยอดนอกระบบก่อน ถ้าไม่พอจึงหักจากยอดในระบบ ·
            สถานะ “หักยอดแล้ว” ตั้งจากหน้า Lab (หักยอดค่า LAB) และล็อกงาน Lab ถาวร
          </span>
        </p>
      </div>

      {downloading && (
        <DfTransferModal
          year={Number(year)}
          month={Number(month)}
          period={period}
          onClose={() => setDownloading(false)}
        />
      )}

      {asking && (
        <ConfirmDialog
          title={`คำนวณรายได้ ${period} ใหม่?`}
          message={
            `ระบบจะคำนวณ DF ของหมอทุกคนจากรายการรักษาในเดือนนี้ใหม่ทั้งหมด` +
            `\n• งาน Lab ในเครือ (LAB SSDC / office SSDC) ของเดือนก่อนที่ยังไม่ตัดยอด จะถูกย้ายมาตัดในเดือนนี้` +
            (anySettled ? `\n• เดือนนี้มีรายการที่ “หักยอดแล้ว” — ตัวเลขจะถูกคำนวณใหม่ แต่สถานะยังคงล็อกอยู่` : '') +
            `\n• หากมีการรักษาที่ยังไม่ระบุชื่อหมอ ระบบจะไม่คำนวณและแจ้ง id ของรายการนั้น`
          }
          confirmLabel="คำนวณรายได้"
          onConfirm={runGen}
          onCancel={() => setAsking(false)}
        />
      )}

      {open && (
        <SalaryDetailDrawer
          row={open}
          doctor={open._doc}
          title={open._code}
          period={period}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  )
}

function hasManagerRole(me) {
  const roles = Array.isArray(me?.roles) ? me.roles : []
  return roles.map((r) => String(r).toLowerCase().replace(/[\s._-]+/g, '')).some((r) => r === 'manager' || r === 'gm')
}

// Xano returns its precondition failures as {message}; keep the Thai text intact.
function xanoMsg(e) {
  const raw = String(e?.message || e || '')
  const m = raw.match(/\{[\s\S]*\}$/)
  if (m) {
    try {
      const j = JSON.parse(m[0])
      if (j?.message) return j.message
    } catch { /* fall through */ }
  }
  return raw
}

// Right-aligned sortable money header. The arrow only shows on the active
// column; the others reveal a muted one on hover so the affordance is findable
// without eight arrows competing for attention.
function SortTh({ k, label, sortKey, sortDir, onSort, className = '' }) {
  const on = sortKey === k
  return (
    <th className={`px-4 py-2.5 text-right ${className}`}>
      <button
        type="button"
        onClick={() => onSort(k)}
        className={`group inline-flex items-center justify-end gap-1 w-full whitespace-nowrap transition ${
          on ? 'text-brand-700 font-semibold' : 'hover:text-slate-700'
        }`}
      >
        {label}
        {on
          ? (sortDir === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />)
          : <ChevronsUpDown size={12} className="opacity-0 group-hover:opacity-60" />}
      </button>
    </th>
  )
}

function Stat({ label, value, unit, hint, tone }) {
  const valueCls = tone === 'amber' ? 'text-amber-700' : tone === 'brand' ? 'text-brand-700' : 'text-slate-900'
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5">
      <div className="text-[11.5px] text-slate-400">{label}</div>
      <div className={`text-[16px] font-semibold tabular-nums ${valueCls}`}>
        {value}{unit && <span className="text-[12px] font-normal text-slate-400 ml-1">{unit}</span>}
      </div>
      {hint && <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  )
}

function Avatar({ src, name }) {
  const [broken, setBroken] = useState(false)
  if (src && !broken) {
    return <img src={src} alt="" onError={() => setBroken(true)} className="w-8 h-8 rounded-full object-cover ring-1 ring-slate-200 shrink-0" />
  }
  return (
    <span className="w-8 h-8 rounded-full bg-brand-50 text-brand-600 text-[12.5px] font-semibold flex items-center justify-center shrink-0">
      {name ? nameInitial(name) : <Stethoscope size={14} />}
    </span>
  )
}
